import * as React from 'react'
import { useState } from 'react'

import { useQuery } from '@apollo/client'
import { Button } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import { FetchEmployeesSettingsDocument } from 'generated/resources/graphql'
import type moment from 'moment'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import DayPicker from 'modules/core/components/Inputs/DayPicker'
import { LeaveTypeSelectorInput } from 'modules/core/components/Inputs/LeaveTypeSelectorInput'
import Select from 'modules/core/components/Inputs/Select'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { stringToDate } from 'modules/core/lib/dates'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'
import { EventTypes, dispatch } from 'modules/core/lib/pubSub'
import LegalEntity from 'modules/core/store/models/LegalEntity'

import OrdinanceAlert from 'modules/attendance/components/TimeTracking/Modals/Exports/OrdinanceAlert'
import {
  FILE_FORMATS,
  TIME_FORMATS,
  TIME_PERIODS,
  TYPES,
  fileFormatAvailableOptions,
  timeFormatOptions,
  timePeriodOptions,
  useTypeAvailableOptions,
} from 'modules/attendance/components/TimeTracking/Modals/Exports/options'
import { sendReportFile } from 'modules/attendance/components/TimeTracking/Modals/Exports/sendReportFileAction'
import { useAttendanceDateNavigator } from 'modules/attendance/lib/attendanceDateNavigatorContext'
import EmployeesSettingsUtils from 'modules/attendance/lib/utils/EmployeesSettingsUtils'
import leaveTypes from 'modules/timeoff/store/collections/leaveTypes'

class ExportsForm extends Form {
  constructor(values: Record<string, string | string[]>) {
    super(values, {
      start_on: 'date',
      end_on: 'date',
      type: 'string',
      file_format: 'string',
      time_format: 'string',
      time_period: 'string',
      leave_type_ids: 'any',
    })
  }

  showLeaveSelector(): boolean {
    return [TYPES.full_version, TYPES.pivot_table].includes(this.get('type').value)
  }

  excelDefaultExport(): boolean {
    const typeValue = this.get('type').value
    const fileValue = this.get('file_format').value

    return typeValue === TYPES.default_export && fileValue === FILE_FORMATS.excel
  }

  showTimeFormat(): boolean {
    const typeValue = this.get('type').value

    const allowedTypes = [
      TYPES.full_version,
      TYPES.full_version_summary,
      TYPES.workplaces_geolocation,
    ]

    return allowedTypes.includes(typeValue) || this.excelDefaultExport()
  }

  showTimePeriod(): boolean {
    const typeValue = this.get('type').value

    const allowedTypes = [TYPES.default_export]

    return allowedTypes.includes(typeValue)
  }
}

interface Props {
  employeeId: string
  onClose: () => void
}

const startOnDate = (form: ExportsForm): moment.Moment => {
  return stringToDate(form.get('start_on').value, 'ui')
}

const endOnDate = (form: ExportsForm): moment.Moment => {
  return stringToDate(form.get('end_on').value, 'ui')
}

const Exports = ({ employeeId, onClose }: Props) => {
  const { stringDateFrom, stringDateTo, cycle } = useAttendanceDateNavigator()
  const { employee } = useCurrentSession()
  const hasBrazilianLegalEntity = !!(
    employee && employee.legalEntity?.country == LegalEntity.countryDefaults.br
  )
  const defaultType = TYPES.full_version
  const defaultFormatType = FILE_FORMATS.excel
  const defaultTimeFormat = TIME_FORMATS.hours_and_minutes
  const defaultTimePeriod = cycle === 'weekly' ? TIME_PERIODS.week : TIME_PERIODS.month

  const [leaveTypeIds, setSelectedLeaveTypes] = useState<string[]>([])

  const { data: employeesSettings } = useQuery(FetchEmployeesSettingsDocument, {
    variables: {
      startOn: stringDateFrom,
      endOn: stringDateTo,
      employeeIds: [employeeId],
    },
  })

  const hasGeoLocationExport = EmployeesSettingsUtils.hasGeoLocation(
    employeesSettings?.attendance?.employeesSettingsConnection?.nodes ?? []
  )

  const form = React.useRef(
    new ExportsForm({
      start_on: stringDateFrom,
      end_on: stringDateTo,
      type: defaultType,
      time_format: defaultTimeFormat,
      time_period: defaultTimePeriod,
      file_format: defaultFormatType,
      leave_type_ids: [],
    })
  ).current

  const { type, time_format, time_period, file_format } = form.data()

  const exportTypeOptions = useTypeAvailableOptions({
    hasBrazilianLegalEntity,
    hasGeoLocationExport,
    clockInOption: true,
  })

  const { running: submitting, performAction: onSubmit } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      await sendReportFile({
        startOn: startOnDate(form),
        endOn: endOnDate(form),
        employeeSelection: { type: 'manual', ids: [employeeId] },
        leaveTypeIds: leaveTypeIds,
        timeFormat: time_format,
        timePeriod: time_period,
        fileType: type,
        fileFormat: file_format,
      })

      closePortal()
      dispatch({
        type: EventTypes.TIMETRACKING_REPORT_DOWNLOADED,
      })
    },
    {
      successFlash: true,
      successMessage: i18n.t('clock_in.modals.export.notice_text'),
    }
  )

  const getFileFormatValue = (): string => {
    const field = form.get('file_format')
    const option = fileFormatAvailableOptions(form.get('type').value)

    if (option.length === 1) {
      field.set(option[0]?.value)
    } else {
      field.set(field.value)
    }

    return field.value
  }

  useAsyncData(async () => {
    await leaveTypes.fetch()
  }, [])
  const visibleLeaveTypes = leaveTypes.active

  const onSelectLeaveTypes = (ids: string[]) => {
    setSelectedLeaveTypes(ids)
  }

  const forfaitjourEnabled = (): boolean => hasFeature('dev_forfaitjour_in_time_management')

  return (
    <Modal
      illustration="clock-app"
      title={i18n.t('clock_in.modals.export.title')}
      description={i18n.t('clock_in.modals.export.description')}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <Button disabled={submitting} submit>
          {i18n.t('common.submit')}
        </Button>
      }
    >
      {hasBrazilianLegalEntity && (
        <FormRow>
          <OrdinanceAlert />
        </FormRow>
      )}
      <FormRow>
        <Select
          label={i18n.t('clock_in.modals.export.options.export_type')}
          field={form.get('type')}
          options={exportTypeOptions}
        />
      </FormRow>
      <FormRow>
        <Select
          label={i18n.t('clock_in.modals.export.options.file_format')}
          field={form.get('file_format')}
          options={fileFormatAvailableOptions(form.get('type').value)}
          value={getFileFormatValue()}
          readonly={fileFormatAvailableOptions(form.get('type').value).length === 1}
        />
      </FormRow>
      {form.showTimeFormat() && (
        <FormRow>
          <Select
            label={i18n.t('clock_in.modals.export.options.time_format_label')}
            description={
              forfaitjourEnabled()
                ? i18n.t('clock_in.modals.export.options.forfait_jours_disclaimer')
                : undefined
            }
            field={form.get('time_format')}
            options={timeFormatOptions()}
          />
        </FormRow>
      )}
      {form.showTimePeriod() && (
        <FormRow>
          <Select
            label={i18n.t('clock_in.modals.export.options.time_period')}
            field={form.get('time_period')}
            options={timePeriodOptions()}
          />
        </FormRow>
      )}

      {form.showLeaveSelector() && (
        <FormRow>
          <LeaveTypeSelectorInput
            label={i18n.t('time_tracking.modals.export.absence_types')}
            attachment="middle center"
            leaveTypes={visibleLeaveTypes}
            selectedIds={leaveTypeIds}
            onSubmit={onSelectLeaveTypes}
            showCreateLeave={visibleLeaveTypes.length === 0}
          />
        </FormRow>
      )}
      <FormRow multi>
        <DayPicker field={form.get('start_on')} label={i18n.t('common.from')} />
        <DayPicker
          field={form.get('end_on')}
          label={i18n.t('common.to')}
          minDate={stringToDate(form.get('start_on').value, 'ui')}
        />
      </FormRow>
    </Modal>
  )
}

export default observer(Exports)
